import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bot.requirementTemplates,
        null,
        navigate,
      );

      if (response.ok) {
        const templatesData = response.data.results || response.data || [];

        // Fetch requirement items count for each template
        const templatesWithCounts = await Promise.all(
          templatesData.map(async (template) => {
            try {
              const itemsResponse = await Utilities.fetchWithAuth(
                "GET",
                `${domainSettings.urls.bot.requirementItems}?requirement_template=${template.id}`,
                null,
                navigate,
              );

              if (itemsResponse.ok) {
                const items =
                  itemsResponse.data.results || itemsResponse.data || [];
                return {
                  ...template,
                  requirement_items: items,
                  item_count: items.length,
                };
              }

              return { ...template, requirement_items: [], item_count: 0 };
            } catch (error) {
              console.error(
                `Error fetching items for template ${template.id}:`,
                error,
              );
              return { ...template, requirement_items: [], item_count: 0 };
            }
          }),
        );

        setTemplates(templatesWithCounts);
      } else {
        Utilities.showError(response, "Failed to fetch templates");
      }
    } catch (error) {
      message.error("An error occurred while fetching templates");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const checkTemplateAssignments = useCallback(
    async (templateId) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "GET",
          `${domainSettings.urls.bot.clearanceAssignments}?requirement_template=${templateId}`,
          null,
          navigate,
        );

        if (response.ok) {
          const assignments = response.data.results || response.data || [];
          return assignments.length;
        }
        return 0;
      } catch (error) {
        console.error("Error checking assignments:", error);
        return 0;
      }
    },
    [navigate],
  );

  const deleteTemplate = useCallback(
    async (templateId) => {
      try {
        // First check if template has any assignments
        const assignmentCount = await checkTemplateAssignments(templateId);

        if (assignmentCount > 0) {
          message.warning({
            content: `Cannot delete template: It has ${assignmentCount} assignment(s). Please deactivate it instead.`,
            duration: 5,
          });
          return false;
        }

        // No assignments, proceed with deletion
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          domainSettings.urls.bot.requirementTemplateDetail.replace(
            ":id",
            templateId,
          ),
          null,
          navigate,
        );

        if (response.ok) {
          message.success("Template deleted successfully");
          fetchTemplates();
          return true;
        } else {
          Utilities.showError(response, "Failed to delete template");
          return false;
        }
      } catch (error) {
        message.error("An error occurred");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchTemplates, checkTemplateAssignments],
  );

  const toggleTemplateStatus = useCallback(
    async (templateId, currentStatus) => {
      try {
        const newStatus = currentStatus === 1 ? 0 : 1;
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          domainSettings.urls.bot.requirementTemplateDetail.replace(
            ":id",
            templateId,
          ),
          { is_active: newStatus },
          navigate,
        );

        if (response.ok) {
          message.success(
            `Template ${newStatus === 1 ? "activated" : "deactivated"} successfully`,
          );
          fetchTemplates();
          return true;
        } else {
          Utilities.showError(response, "Failed to update template status");
          return false;
        }
      } catch (error) {
        message.error("An error occurred");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchTemplates],
  );

  const filteredTemplates = templates.filter(
    (template) =>
      template.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (template.class_name &&
        template.class_name.toLowerCase().includes(searchText.toLowerCase())) ||
      (template.acadyr &&
        template.acadyr.toLowerCase().includes(searchText.toLowerCase())),
  );

  return {
    templates,
    filteredTemplates,
    loading,
    searchText,
    setSearchText,
    fetchTemplates,
    deleteTemplate,
    toggleTemplateStatus,
  };
};
